import _ from 'underscore';
import { Component } from 'react';
import styled from 'styled-components';
import { P, Tooltip, Icon, Checkbox } from '@workramp/ui';
import { StyledLinkText } from '~/styled-components/admin/wrappers';
import Modal from '~/components/common/modal';
import MultiInputField from '~/components/common/multi-input-field';
import { showLoader, hideLoader, MixPanel } from '~/common';
import { APP_CONSTANTS, I18n } from '~/common/explicit-exports';
import { WRModals } from '~/components/wr-modals/wr-modals-container';
import AcademySegmentsApi from '../../../../agent/academy-segments';
import ActionMessages from '../../../widget/action-messages';

const ALL = 'all';
const INCLUDE = 'include';
const EXCLUDE = 'exclude';
export default class EditSegmentVisibilityModal extends Component {
  constructor(props) {
    super(props);
    let selectedVisibility = ALL;
    if (props.visibleSegments && !_.isEmpty(props.visibleSegments)) {
      selectedVisibility = props.includeSegments ? INCLUDE : EXCLUDE;
    }
    this.state = {
      selectedVisibility,
      currentSegmentToAdd: null,
      visibleSegments: props.visibleSegments || [],
      hasLoaded: props.hasLoaded,
      visibleToLoggedOutUsers: props.visibleToLoggedOutUsers || false,
      visibleToLoggedOutUsersDisabled: props.visibleToLoggedOutUsersDisabled,
      visibleToLoggedOutUsersShown: props.visibleToLoggedOutUsersShown || false,
    };
  }

  componentDidMount() {
    const { academyId, objectId, objectType, hasLoaded, includeSegments } = this.props;
    if (hasLoaded) return;

    AcademySegmentsApi.fetchObjectSegments(academyId, objectId, objectType).then((fetchedSegments) => {
      let selectedVisibility = ALL;
      if (!_.isEmpty(fetchedSegments)) {
        selectedVisibility = includeSegments ? INCLUDE : EXCLUDE;
      }
      this.setState({
        hasLoaded: true,
        selectedVisibility,
        fetchedSegments,
      });
    });
  }

  render() {
    return (
      <Modal
        show={this.props.show}
        title="Visibility Permissions"
        onSave={this.onSave}
        onClose={this.props.onClose}
        disableSaveOnEnter
        dontShowSaveButton={!this.props.hasSegments && !this.state.visibleToLoggedOutUsersShown}
      >
        <div className="my-form">
          {this.state.hasLoaded ? (
            <>
              {this.renderVisibleToAll()}
              {this.renderSegmentsInput(true)}
              {this.renderSegmentsInput(false)}
            </>
          ) : (
            <LoadingImgWrapper>
              <img className="pal" height="50" width="50" src="/images/loader.gif" />
            </LoadingImgWrapper>
          )}
        </div>
      </Modal>
    );
  }

  handleVisibilityChange = () => {
    this.setState({ selectedVisibility: ALL });
    MixPanel.track(APP_CONSTANTS.MIXPANEL_TRACKING_KEYS.TOGGLE_SEGMENT_VISIBILITY, {
      objectType: this.props.objectType,
      setting: 'all',
    });
  };

  handleLoggedOutChange = (isSelected) => {
    this.setState({ visibleToLoggedOutUsers: isSelected });
  };

  renderVisibleToAll = () => (
    <div className="form-field">
      <label className="cursor-pointer">
        <input
          name="visibility"
          type="radio"
          className="pvs prs fss1"
          checked={this.state.selectedVisibility === ALL}
          onChange={this.handleVisibilityChange}
        />
        <span className="mlm">Visible to all users</span>
      </label>
      {this.state.visibleToLoggedOutUsersShown && this.state.selectedVisibility === ALL && (
        <VisibleToLoggedOutUsersWrapper>
          <Checkbox
            defaultSelected={this.state.visibleToLoggedOutUsers}
            description={
              this.state.visibleToLoggedOutUsersDisabled ||
              I18n.t('js.admin.academies.visibility.logged_out_description')
            }
            isDisabled={!!this.state.visibleToLoggedOutUsersDisabled}
            onChange={this.handleLoggedOutChange}
            // tooltip={this.state.visibleToLoggedOutUsersDisabled}
          >
            Visible to logged out users
          </Checkbox>
        </VisibleToLoggedOutUsersWrapper>
      )}
    </div>
  );

  // Explicitly clear out segments on change, in case we're switching from include to exclude.
  toggleChange = (include) => {
    const { objectType } = this.props;
    MixPanel.track(APP_CONSTANTS.MIXPANEL_TRACKING_KEYS.TOGGLE_SEGMENT_VISIBILITY, {
      objectType,
      setting: include ? 'include' : 'exclude',
    });
    this.setState({
      visibleSegments: [],
      selectedVisibility: include ? INCLUDE : EXCLUDE,
      visibleToLoggedOutUsers: false,
    });
  };

  renderSegmentsInput = (include = true) => {
    const stateSegmentNames = _.map(this.state.visibleSegments, (segment) => segment.label);

    const isChecked =
      (include && this.state.selectedVisibility === INCLUDE) || (!include && this.state.selectedVisibility === EXCLUDE);

    return (
      <div className="form-field">
        <label className="cursor-pointer">
          <input
            name="visibility"
            type="radio"
            className="pvs prs fss1"
            checked={isChecked}
            onChange={() => this.toggleChange(include)}
            disabled={!this.props.hasSegments}
          />
          <span className="mlm">{include ? `Visible to specific segments` : `Exclude specific segments`}</span>
          {!include && (
            <TooltipWrapper>
              <Tooltip zIndex={10000} message="Excludes any unauthenticated visitors">
                <Icon name="info:regular" />
              </Tooltip>
            </TooltipWrapper>
          )}
        </label>
        {isChecked && this.props.hasSegments && (
          <MultiInputField
            autoCompleteUrl={`/api/academy_segments/${this.props.academyId}/search`}
            input={this.state.currentSegmentToAdd}
            forceSelectFromSuggestions
            name={null}
            onChange={this.onSegmentsChange}
            onFocus={() => this.setState({ selectedVisibility: include ? INCLUDE : EXCLUDE })}
            onInput={this.onSegmentInput}
            placeholder="Enter Segments"
            tags={stateSegmentNames}
            tagsForSuggestionFiltration={stateSegmentNames}
            tagItemName="Segment"
          />
        )}
        {isChecked && !this.props.hasSegments && this.renderNoSegmentsSection()}
      </div>
    );
  };

  renderNoSegmentsSection = () => (
    <NoSegmentsSectionWrapper>
      <NoSegmentsInput type="text" readOnly disabled value="No Segments have been added to this Academy." />
      <NoSegmentAddlTxt density="lg">
        Segments allow for partitioned viewing of Academy content.
        <br />
        <a
          href={`/admin/customer_learning_cloud_hub/${this.props.clcHubId}/contacts/segments`}
          target="_blank"
          rel="noreferrer"
        >
          <StyledLinkText size={2}>Click here</StyledLinkText>
        </a>{' '}
        to get started.
      </NoSegmentAddlTxt>
    </NoSegmentsSectionWrapper>
  );

  onSegmentsChange = (item, segmentToAdd, segmentToRemove) => {
    const { selectedVisibility } = this.state;
    let visibleSegments = JSON.parse(JSON.stringify(this.state.visibleSegments));

    if (item) {
      visibleSegments.push(item);
      MixPanel.track(APP_CONSTANTS.MIXPANEL_TRACKING_KEYS.ADD_SEGMENT, {
        objectType: this.props.objectType,
        setting: selectedVisibility,
      });
      this.forceUpdate();
    } else if (segmentToAdd) {
      visibleSegments.push(segmentToAdd);
      MixPanel.track(APP_CONSTANTS.MIXPANEL_TRACKING_KEYS.ADD_SEGMENT, {
        objectType: this.props.objectType,
        setting: selectedVisibility,
      });
      this.forceUpdate();
    } else if (segmentToRemove) {
      // Remove the tag from the list of tags to add
      visibleSegments = _.filter(
        visibleSegments,
        (visibleSegment) => visibleSegment.label.toLowerCase() != segmentToRemove.toLowerCase(),
      );
      MixPanel.track(APP_CONSTANTS.MIXPANEL_TRACKING_KEYS.REMOVE_SEGMENT, {
        objectType: this.props.objectType,
        setting: selectedVisibility,
      });
      this.forceUpdate();
    }

    this.setState({
      visibleSegments,
      currentSegmentToAdd: null,
    });
  };

  onSegmentInput = (segment) => {
    this.setState({
      currentSegmentToAdd: segment,
    });
    this.forceUpdate();
  };

  onSave = (onFinish) => {
    const { selectedVisibility, visibleSegments, visibleToLoggedOutUsers } = this.state;
    const { academyId, objectId, objectType } = this.props;
    const data = {
      segment_ids: selectedVisibility !== ALL ? _.map(visibleSegments, (segment) => segment.id) : [],
      object_id: objectId,
      object_type: objectType,
      segment_labels: selectedVisibility !== ALL ? [this.state.currentSegmentToAdd] : [],
      included: selectedVisibility === INCLUDE,
      visible_to_logged_out_users: visibleToLoggedOutUsers,
    };

    showLoader();
    AcademySegmentsApi.updateObjectSegments(academyId, data).then((resp) => {
      hideLoader();
      onFinish();
      const savedSegments = resp.academySegments;
      let include = null;
      if (selectedVisibility !== ALL) include = selectedVisibility === INCLUDE;
      this.props.onSave(selectedVisibility !== ALL ? savedSegments : [], include, visibleToLoggedOutUsers);
      if (selectedVisibility !== ALL && !_.isEmpty(resp.invalidLabels)) {
        const alertMessage = [];
        // show successfully applied segments first (if any)
        if (!_.isEmpty(savedSegments)) {
          alertMessage.push(
            `The following segments were assigned successfully:\n${_.map(savedSegments, (s) => s.label).join('\n')}`,
          );
        }
        // then add the segment label that couldn't be found
        alertMessage.push(`Segment with the name "${resp.invalidLabels[0]}" could not be found and was not assigned`);
        WRModals.alert(alertMessage.join('\n\n'));
      } else {
        // Content is visible to all users, or no invalidLabels were returned
        ActionMessages.generateMessage({ message: 'Update Successful' });
      }
    });
  };
}

const TooltipWrapper = styled.div`
  display: inline;
  margin-left: 5px;
`;

const LoadingImgWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const NoSegmentsSectionWrapper = styled.div.attrs({
  className: 'form-field',
})``;

const NoSegmentsInput = styled.input`
  font-style: italic;
  width: 100%;
  border: none;
  border-bottom: 1px solid rgba(118, 118, 118, 0.3);
  padding-left: 5px;
`;

const NoSegmentAddlTxt = styled(P)`
  margin-top: 10px;
  padding-left: 5px;
`;

const VisibleToLoggedOutUsersWrapper = styled.div`
  // form field has a 30px bottom margin
  margin-bottom: calc(-30px + ${({ theme }) => theme.spacing(4)}px);
  margin-top: ${({ theme }) => theme.spacing(4)}px;
  padding-left: ${({ theme }) => theme.spacing(6)}px;
`;
